import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import axios from "axios";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import {
  OverviewColumn,
  OverviewPagination,
  OverviewTable,
  StatPanel,
  StatTile,
  money,
} from "../shared/AffiliateOverview";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "stretch",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    padding: "2em",
  },
  mainHeading: {
    textAlign: "center",
    marginBottom: "1em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  card: {
    width: "100%",
    maxWidth: 1200,
    marginTop: "1em",
    padding: "2em",
    alignSelf: "center",
  },
  form: {
    padding: "0 1em 1em 1em",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1em",
  },
  input: {
    marginTop: "1em",
    width: "100%",
    maxWidth: 420,
  },
  actions: {
    padding: "0 1em 1em 1em",
    display: "flex",
    gap: "1em",
    justifyContent: "center",
  },
  tableContainer: {
    marginTop: "1.5em",
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    overflowX: "auto",
    padding: "0 1.5em 1.5em 1.5em",
  },
  transactionContainer: {
    marginTop: "1.5em",
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    overflowX: "auto",
    padding: "0 1.5em 1.5em 1.5em",
  },
  table: {
    width: "100%",
  },
  tableCell: {
    padding: "8px 16px",
    border: "1px solid #e0e0e0",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  sectionTitle: {
    marginTop: "2em",
    marginBottom: "1em",
    color: theme.palette.primary.main,
  },
  noDataMessage: {
    textAlign: "center",
    color: theme.palette.grey[500],
    fontStyle: "italic",
    margin: "2em 0",
  },
  helperText: {
    color: theme.palette.grey[600],
    marginTop: "0.5em",
  },
}));

interface PartnerizeUserDetails {
  pubref: string | null;
  user_id: number;
  sprive_plus_status: string;
  sprive_plus_enabled: boolean;
}

interface PartnerizeTransaction {
  [key: string]: any;
}

interface PartnerizeApiResponse {
  status: boolean;
  message: string;
  data: Array<{
    userDetails: PartnerizeUserDetails[];
    transactionDetails: PartnerizeTransaction[];
  }>;
}

interface PartnerizeStats {
  purchases: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    mixed: number;
    approval_rate_pct: number;
  };
  value: {
    total_conversion_value: number;
    approved_conversion_value: number;
    total_commission: number;
    approved_commission: number;
    paid_commission: number;
  };
  rewards: {
    total: number;
    pending: number;
    earned: number;
    transferred: number;
    cancelled: number;
    failed: number;
    user_amount: number;
    paid_to_users: number;
  };
  reach: {
    distinct_users: number;
    distinct_campaigns: number;
  };
}

interface PartnerizeTopBrand {
  brand_name: string | null;
  campaign_id: string;
  purchases: number;
  approved_count: number;
  conversion_value: number;
  commission: number;
}

const TOP_BRAND_COLUMNS: OverviewColumn[] = [
  { key: "brand_name", label: "Brand" },
  { key: "campaign_id", label: "Campaign ID" },
  { key: "purchases", label: "Purchases" },
  { key: "approved_count", label: "Approved" },
  { key: "conversion_value", label: "Sales", money: true },
  { key: "commission", label: "Commission", money: true },
];

const RECENT_COLUMNS: OverviewColumn[] = [
  { key: "conversion_id", label: "Conversion ID" },
  { key: "email", label: "User" },
  { key: "brand_name", label: "Brand" },
  { key: "conversion_value", label: "Sale", money: true },
  { key: "publisher_commission", label: "Commission", money: true },
  { key: "conversion_status", label: "Status (Partnerize)" },
  { key: "conversion_time", label: "Conversion Date", date: true },
  { key: "approved_at", label: "Approved", date: true },
  { key: "rewards_amount", label: "Reward", money: true },
  { key: "reward_status", label: "Reward Status (Sprive)" },
];

const RELEASABLE_STATUS = "pending";

const PartnerizeUserActivity = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [userDetails, setUserDetails] = useState<PartnerizeUserDetails | null>(null);
  const [transactions, setTransactions] = useState<PartnerizeTransaction[]>([]);
  const [headers, setHeaders] = useState<{ [key: string]: string }>({});
  const [hasDetails, setHasDetails] = useState(false);

  const [stats, setStats] = useState<PartnerizeStats | null>(null);
  const [topBrands, setTopBrands] = useState<PartnerizeTopBrand[]>([]);
  const [recent, setRecent] = useState<any[]>([]);
  const [pagination, setPagination] = useState<OverviewPagination | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [editRow, setEditRow] = useState<PartnerizeTransaction | null>(null);
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const classes = useStyles();
  const notify = useNotify();

  const formatUTCDateTime = (value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const pad = (num: number) => String(num).padStart(2, "0");
    return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}, ${pad(
      date.getUTCHours()
    )}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  };

  const toInputValue = (value: string | null) => {
    const date = value ? new Date(value) : new Date();
    if (isNaN(date.getTime())) return "";
    const pad = (num: number) => String(num).padStart(2, "0");
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
      date.getUTCDate()
    )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  };

  const getHeaderLabel = (key: string, defaultLabel: string) => {
    if (key === "conversion_status") return `${defaultLabel} (Partnerize)`;
    if (key === "reward_status") return `${defaultLabel} (Sprive)`;
    return defaultLabel;
  };

  const loadOverview = useCallback(
    async (nextPage = page, nextSize = pageSize) => {
      try {
        const config = getConfigGet({ page: nextPage, limit: nextSize });
        const response = await axios.get(ENDPOINTS.USER_PARTNERIZE_ACTIVITY, config);
        const firstData = (response?.data as any)?.data?.[0];
        if (!firstData) return;
        setStats(firstData.stats ?? null);
        setTopBrands(firstData.top_brands ?? []);
        setRecent(firstData.recent_purchases ?? []);
        setPagination(firstData.pagination ?? null);
      } catch (error: any) {
        console.error("Error fetching Partnerize overview:", error);
      }
    },
    [page, pageSize]
  );

  useEffect(() => {
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    if (hasDetails) {
      loadOverview(nextPage, pageSize);
    } else {
      loadOverview(nextPage, pageSize);
    }
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
    loadOverview(1, size);
  };

  const renderStats = () => {
    if (!stats) return null;

    const tiles: StatTile[] = [
      { value: stats.purchases.total, label: "Conversions" },
      { value: stats.purchases.pending, label: "Pending" },
      { value: stats.purchases.approved, label: "Approved" },
      { value: stats.purchases.rejected, label: "Rejected" },
      { value: stats.purchases.mixed, label: "Mixed" },
      {
        value: `${stats.purchases.approval_rate_pct}%`,
        label: "Approval rate",
        sub: "observed, not Partnerize's figure",
      },
    ];

    const valueTiles: StatTile[] = [
      { value: money(stats.value.total_conversion_value), label: "Total sales" },
      { value: money(stats.value.approved_conversion_value), label: "Approved sales" },
      { value: money(stats.value.total_commission), label: "Commission (all)" },
      { value: money(stats.value.approved_commission), label: "Commission (approved)" },
      { value: money(stats.value.paid_commission), label: "Paid by Partnerize" },
      { value: money(stats.rewards.user_amount), label: "Owed to users" },
      { value: money(stats.rewards.paid_to_users), label: "Paid to users" },
      { value: stats.reach.distinct_users, label: "Users with purchases" },
    ];

    return <StatPanel title="Partnerize Performance" tiles={tiles} valueTiles={valueTiles} />;
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    setLoading(true);
    setUserDetails(null);
    setTransactions([]);
    setHeaders({});
    setHasDetails(false);

    try {
      const config = getConfigGet({ email: email.trim(), page, limit: pageSize });
      const response = await axios.get(ENDPOINTS.USER_PARTNERIZE_ACTIVITY, config);

      const responseData = response.data as PartnerizeApiResponse;
      const firstData = responseData?.data?.[0] as any;

      setStats(firstData?.stats ?? null);
      setTopBrands(firstData?.top_brands ?? []);
      setRecent(firstData?.recent_purchases ?? []);
      setPagination(firstData?.pagination ?? null);

      if (responseData?.status && firstData?.userDetails?.length) {
        setUserDetails(firstData.userDetails[0]);
        setHasDetails(true);

        if (firstData.transactionDetails?.length) {
          const headerRow = firstData.transactionDetails[0];
          const headerMapping: { [key: string]: string } = {};
          Object.keys(headerRow).forEach((key) => {
            headerMapping[key.toLowerCase()] = headerRow[key];
          });
          setHeaders(headerMapping);
          setTransactions(firstData.transactionDetails.filter((_: any, index: number) => index > 0));
        } else {
          setHeaders({});
          setTransactions([]);
        }

        notify("Partnerize details found successfully", "info");
      } else {
        setHasDetails(false);
        notify(responseData?.message || "No Partnerize details found for this email", "warning");
      }
    } catch (error: any) {
      console.error("Error fetching Partnerize details:", error);
      notify(error?.response?.data?.message || "Failed to fetch Partnerize details", "error");
      setHasDetails(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToPartnerize = async () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    setAddLoading(true);
    try {
      const config = getConfigPost();
      await axios.post(
        ENDPOINTS.USER_PARTNERIZE_ACTIVITY,
        {},
        { headers: config, params: { email: email.trim() } }
      );
      notify("Sprive Plus enabled - this unlocks Partnerize and VDX brands", "info");
      handleEmailSubmit();
    } catch (error: any) {
      console.error("Error enabling Sprive Plus:", error);
      notify(error?.response?.data?.message || "Failed to enable Sprive Plus", "error");
    } finally {
      setAddLoading(false);
    }
  };

  const openEditDialog = (transaction: PartnerizeTransaction) => {
    setEditRow(transaction);
    setEditDate(toInputValue(transaction.transaction_date));
  };

  const closeEditDialog = () => {
    setEditRow(null);
    setEditDate("");
  };

  const handleReleaseDateSave = async () => {
    if (!editRow || !editDate) {
      notify("Please pick a transaction date", "error");
      return;
    }

    setSaving(true);
    try {
      const config = getConfigPost();
      await axios.put(
        ENDPOINTS.USER_PARTNERIZE_ACTIVITY,
        {
          reward_id: editRow.reward_id,
          transaction_date: `${editDate.replace("T", " ")}:00`,
        },
        { headers: config, params: { email: email.trim() } }
      );

      notify("Transaction date updated. The next transfer run will pay it.", "info");
      closeEditDialog();
      handleEmailSubmit();
    } catch (error: any) {
      console.error("Error updating transaction date:", error);
      notify(error?.response?.data?.message || "Failed to update transaction date", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderUserDetails = () => {
    if (!hasDetails || !userDetails) return null;

    return (
      <Paper className={classes.tableContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Partnerize User Details
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>User ID</TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>Pubref</TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                Sprive Plus Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableCell}>{userDetails.user_id}</TableCell>
              <TableCell className={classes.tableCell}>
                {userDetails.pubref || "Not clicked a Partnerize brand yet"}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {userDetails.sprive_plus_status}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const renderTransactions = () => {
    if (!hasDetails) return null;

    if (!transactions.length) {
      return (
        <Paper className={classes.transactionContainer}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Partnerize Purchases
          </Typography>
          <Typography className={classes.noDataMessage}>No purchases found</Typography>
        </Paper>
      );
    }

    const keys = Object.keys(headers);

    return (
      <Paper className={classes.transactionContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Partnerize Purchases
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {keys.map((key) => (
                <TableCell key={key} className={`${classes.tableCell} ${classes.tableHeader}`}>
                  {getHeaderLabel(key, headers[key])}
                </TableCell>
              ))}
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction, index) => (
              <TableRow key={index}>
                {keys.map((key) => (
                  <TableCell key={key} className={classes.tableCell}>
                    {(key === "order_value" || key === "rewards_amount") && transaction[key]
                      ? `£${transaction[key]}`
                      : (key.includes("date") || key === "approved_at") && transaction[key]
                        ? formatUTCDateTime(transaction[key])
                        : transaction[key]}
                  </TableCell>
                ))}
                <TableCell className={classes.tableCell}>
                  <Tooltip
                    title={
                      !transaction.reward_id
                        ? "No reward row for this purchase yet"
                        : transaction.reward_status !== RELEASABLE_STATUS
                          ? `Already ${transaction.reward_status} - the date can only be changed while pending`
                          : "Change when this reward is released"
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={
                          !transaction.reward_id ||
                          transaction.reward_status !== RELEASABLE_STATUS
                        }
                        onClick={() => openEditDialog(transaction)}
                      >
                        Edit Transaction Date
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  return (
    <div className={classes.main}>
      <Typography variant="h3" component="h1" className={classes.mainHeading}>
        Partnerize User Activity
      </Typography>

      <Paper className={classes.card}>
        <div className={classes.form}>
          <TextField
            className={classes.input}
            label="Enter Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            fullWidth
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleEmailSubmit();
            }}
          />

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEmailSubmit}
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Searching..." : "Search Partnerize Activity"}
            </Button>

            <Tooltip
              title={
                userDetails?.sprive_plus_enabled
                  ? "Sprive Plus is already enabled for this user"
                  : "Enables Sprive Plus, which unlocks both Partnerize and VDX brands"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleAddToPartnerize}
                  disabled={
                    addLoading || !email.trim() || !!userDetails?.sprive_plus_enabled
                  }
                  startIcon={addLoading ? <CircularProgress size={20} /> : null}
                >
                  {addLoading ? "Adding..." : "Add to Partnerize"}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>

        {!hasDetails && email && !loading && (
          <Typography className={classes.noDataMessage}>
            No Partnerize activity found for this email
          </Typography>
        )}
      </Paper>

      {renderStats()}
      {renderUserDetails()}
      {renderTransactions()}
      <OverviewTable
        title="Top Brands by Commission"
        columns={TOP_BRAND_COLUMNS}
        rows={topBrands}
        emptyMessage="No brand data yet"
        rowKey={(row) => row.campaign_id}
      />
      <OverviewTable
        title="Latest Conversions"
        columns={RECENT_COLUMNS}
        rows={recent}
        emptyMessage="No conversions yet"
        rowKey={(row, i) => row.purchase_id ?? i}
        pagination={pagination}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <Dialog open={!!editRow} onClose={closeEditDialog}>
        <DialogTitle>Edit Transaction Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {editRow?.brand_name} &mdash; £{editRow?.rewards_amount}
          </Typography>
          <TextField
            className={classes.input}
            label="Transaction Date (UTC)"
            type="datetime-local"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" className={classes.helperText} component="p">
            Setting a date in the past releases the reward on the next affiliate rewards
            transfer run. The money is not moved by saving this.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleReleaseDateSave}
            color="primary"
            variant="contained"
            disabled={saving || !editDate}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default PartnerizeUserActivity;
